import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import clsx from 'clsx';

import SearchInput from '../../../components/common-inputs/SearchInput';
import { SCOUT_TABS, SORT_ORDER } from '../../../constants';
import CommonButton, { BUTTON_STYLES } from '../../../components/common-button/CommonButton';
import Icon from '../../../icons/Icon';
import { ICONS } from '../../../icons';
import useMessagesListSort from '../../../hooks/useMessagesListSort';
import { changeTableSearch, changeTableSort } from '../../../redux/scout/scoutReducer';
import CreateMessageModal from '../create-message-modal/CreateMessageModal';
import ConfirmationModal from '../../../components/confirmation-modal/ConfirmationModal';
import TooltipButton from '../../../components/tooltip-button/TooltipButton';

import scss from './MessageFilters.module.scss';

const MessageFilters = ({
  onClearBoard,
  onClearAll,
  onExportMessages,
  onCreateMessage,
  onReclaim,
  onCollectResponseData,
  onResetSession,
  disableResponseData,
}) => {
  const { t } = useTranslation('scout');
  const dispatch = useDispatch();

  const { id } = useParams();

  const abortCompressAction = useRef(null);
  const abortGenerateMoreAction = useRef(null);

  const [isShowCreateMessageModal, setIsShowCreateMessageModal] = useState(false);
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState(false);

  const isGenerating = useSelector((state) => state.scout.isGenerating);
  const isFormLoading = useSelector((state) => state.scout.isFormLoading);
  const isResetting = useSelector((state) => state.scout.isResetting);
  const scoutTab = useSelector((state) => state.scout.scoutTab);
  const messagesContent = useSelector((state) => state.scout.messagesContent);

  const disableButtons = isFormLoading || isGenerating;

  const { sort, searchQuery } = useSelector((state) => state.scout.tableSettings);
  const sortOptions = useMessagesListSort();

  const handleChangeSort = useCallback(
    ({ sort, direction }) => {
      dispatch(changeTableSort(`${direction === SORT_ORDER.DESC ? '-' : ''}${sort}`));
    },
    [dispatch],
  );

  const handleChangeSearch = useCallback(
    (newSearch) => {
      dispatch(changeTableSearch(newSearch));
    },
    [dispatch],
  );

  const handleOpenCreateMessageModal = useCallback(() => {
    setIsShowCreateMessageModal(true);
  }, []);

  const handleCloseCreateMessageModal = useCallback(() => {
    setIsShowCreateMessageModal(false);
  }, []);

  const handleOpenClearAllModal = () => {
    setIsClearAllModalOpen(true);
  };

  const handleCloseClearAllModal = () => {
    setIsClearAllModalOpen(false);
  };

  const handleClearBoard = () => {
    dispatch(onClearBoard());
  };

  const handleReclaimBackup = () => {
    dispatch(onReclaim(id));
  };

  const handleCLearAll = () => {
    dispatch(onClearAll(id));
  };

  const handleExportMessages = () => {
    dispatch(onExportMessages());
  };

  const handleResetSession = () => {
    dispatch(onResetSession(id));
  };

  const handleCreateMessage = async ({ projectId, messages }) => {
    await dispatch(onCreateMessage({ projectId, messages }));
  };

  useEffect(() => {
    return () => {
      if (abortCompressAction.current) {
        abortCompressAction.current();
      }
      if (abortGenerateMoreAction.current) {
        abortGenerateMoreAction.current();
      }
    };
  }, []);

  if (scoutTab === SCOUT_TABS.BACKUP) {
    return (
      <div className={scss.filtersWrapper}>
        <CommonButton styleColor={BUTTON_STYLES.SECONDARY} maxWidth={150} onClick={handleReclaimBackup}>
          {t('reclaimBackup')}
          <Icon icon={ICONS.RoundArrow} disableCommonColors className={scss.reclaimIcon} />
        </CommonButton>
      </div>
    );
  }

  return (
    <div className={scss.filtersWrapper}>
      <div className={scss.leftSection}>
        <TooltipButton
          styleColor={BUTTON_STYLES.SECONDARY}
          onClick={handleOpenCreateMessageModal}
          disabled={disableButtons}
          tooltip={isGenerating && t('messagesGenerating')}
          className={scss.customMessageButton}>
          <Icon icon={ICONS.Edit} disableCommonColors />
          {t('customMessage')}
        </TooltipButton>
      </div>

      <div className={scss.rightSection}>
        <SearchInput
          value={searchQuery}
          onChange={handleChangeSearch}
          size='sm'
          placeholder={t('searchByWord')}
          maxWidth={280}
          disabled={isGenerating}
          extraWrapperClass={scss.inputMedium}
        />

        {onResetSession ? (
          <TooltipButton
            styleColor={BUTTON_STYLES.SECONDARY}
            onClick={handleResetSession}
            maxWidth={140}
            disabled={disableButtons}
            tooltip={isGenerating && t('messagesGenerating')}>
            {t('resetSession')}
            <Icon icon={ICONS.Reset} className={clsx(isResetting && scss.spinAnimation)} />
          </TooltipButton>
        ) : null}

        {onClearBoard ? (
          <TooltipButton
            styleColor={BUTTON_STYLES.SECONDARY}
            maxWidth={150}
            onClick={handleClearBoard}
            disabled={disableButtons}
            tooltip={isGenerating && t('messagesGenerating')}
            isStrokeIcon>
            {t('clearBoard')}
            <Icon icon={ICONS.Clean} disableCommonColors className={scss.cleanIcon} />
          </TooltipButton>
        ) : null}

        {onClearAll ? (
          <TooltipButton
            styleColor={BUTTON_STYLES.SECONDARY}
            maxWidth={130}
            onClick={handleOpenClearAllModal}
            disabled={disableButtons}
            tooltip={isGenerating && t('messagesGenerating')}
            isStrokeIcon>
            {t('clearAll')}
            <Icon icon={ICONS.Cross} disableCommonColors className={scss.crossIcon} />
          </TooltipButton>
        ) : null}

        <TooltipButton
          styleColor={BUTTON_STYLES.SECONDARY}
          maxWidth={160}
          disabled={disableButtons}
          tooltip={isGenerating && t('messagesGenerating')}
          onClick={handleExportMessages}>
          {t('exportMessages')}
          <Icon icon={ICONS.ArrowUp} disableCommonColors />
        </TooltipButton>

        <TooltipButton
          styleColor={BUTTON_STYLES.SECONDARY}
          maxWidth={190}
          onClick={onCollectResponseData}
          isStrokeIcon
          tooltip={isGenerating && t('messagesGenerating')}
          disabled={!messagesContent.length || disableResponseData || disableButtons}>
          {t('collectResponseData')}
          <Icon icon={ICONS.Aim} disableCommonColors className={scss.aimIcon} />
        </TooltipButton>
      </div>

      <CreateMessageModal
        isOpen={isShowCreateMessageModal}
        onClose={handleCloseCreateMessageModal}
        onCreateMessage={handleCreateMessage}
      />

      <ConfirmationModal
        isOpen={isClearAllModalOpen}
        onClose={handleCloseClearAllModal}
        onCancel={handleCloseClearAllModal}
        onConfirm={handleCLearAll}
        label={t('areYouSureYouWantToDeleteMessageHistory')}
        subLabel={t('thisCanNotBeReversed')}
      />
    </div>
  );
};

export default memo(MessageFilters);
